import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import ContentLoader from 'ui_system/utilities/ContentLoader';
import ClockIcon from 'views/ui/icons/clock-icon';

import { useMediaQuery } from 'react-responsive';
import Truncate from 'react-truncate';

import { t } from 'services/translation';
import {
  Theme,
  cssIEGridSupportColumns,
  cssIEGridSupportRows,
  Grid,
  Button,
  cssWordBreak,
  HR,
} from './styles';

import { EXPERIENCE } from '../routes';

const midCssBreak = '800px';
const smallCssBreak = '600px';

const Card = styled.div`
  border: solid 1px #cecece;
  padding: 15px;
  margin-bottom: 30px;
  color: #333;
  transition: opacity 200ms linear;
  position: relative;
  &:hover {
    color: inherit;
    text-decoration: none;
  }
  @media (max-width: ${smallCssBreak}) {
    padding: 0;
    margin-bottom: 80px;
  }
`;

const CardGrid = styled(Grid)`
  grid-gap: 15px;
  @media (max-width: ${midCssBreak}) {
    grid-template-columns: 210px 1fr;
    /* grid-template-rows: 210px; */
  }
  @media (max-width: ${smallCssBreak}) {
    grid-gap: 15px;
    grid-template-columns: 100%;
    grid-template-rows: ${(props) => (props.isLoading ? '210px' : 'auto')};
  }
  ${cssIEGridSupportColumns};
`;

const Picture = styled.img`
  margin-right: 15px;
  width: 250px;
  height: 250px;
  display: flex;
  align-self: center;
  @media (max-width: ${midCssBreak}) {
    width: 210px;
    height: 210px;
    display: none;
  }
`;

const MobilePicture = styled(Picture)`
  display: none;
  @media (max-width: ${smallCssBreak}) {
    width: 250px;
    height: 150px;
    display: block;
  }
`;

const GradientWrapper = styled.div`
  @media (max-width: ${smallCssBreak}) {
    &:after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      height: 150px;
      z-index: 0;
      background: -moz-linear-gradient(
        -45deg,
        rgba(77, 77, 77, 0) 80%,
        rgba(0, 0, 0, 0.85) 100%
      );
      background: -webkit-gradient(
        linear,
        left top,
        right bottom,
        color-stop(80%, rgba(77, 77, 77, 0)),
        color-stop(100%, rgba(0, 0, 0, 0.85))
      );
      background: -webkit-linear-gradient(
        -45deg,
        rgba(77, 77, 77, 0) 80%,
        rgba(0, 0, 0, 0.85) 100%
      );
      background: -o-linear-gradient(
        -45deg,
        rgba(77, 77, 77, 0) 80%,
        rgba(0, 0, 0, 0.85) 100%
      );
      background: -ms-linear-gradient(
        -45deg,
        rgba(77, 77, 77, 0) 80%,
        rgba(0, 0, 0, 0.85) 100%
      );
      background: linear-gradient(
        135deg,
        rgba(77, 77, 77, 0) 80%,
        rgba(0, 0, 0, 0.85) 100%
      );
    }
  }
`;

const Info = styled(Grid)`
  @media (max-width: ${midCssBreak}) {
    grid-template-rows: 1fr 36px;
  }
  @media (max-width: ${smallCssBreak}) {
    ${(props) => props.isLoading && 'grid-template-rows: 100%'};
  }
  ${cssIEGridSupportRows};
`;

const Name = styled.h1`
  font-size: 18px;
  line-height: 22px;
  font-weight: 600;
  margin: 0 0 9px;

  @media (max-width: ${midCssBreak}) {
    font-size: 16px;
    line-height: 19px;
    margin: 0 0 4px;
  }
  @media (max-width: ${smallCssBreak}) {
    width: 100%;
    font-size: 18px;
    line-height: 21px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Tagline = styled.div`
  font-size: 16px;
  font-weight: ${Theme.fontWeight.regular};
  display: flex;
  align-items: flex-start;
  min-height: 22px;
  @media (max-width: ${midCssBreak}) {
    font-size: 14px;
  }
`;

const Description = styled.div`
  margin: 15px 0 0;
  font-weight: ${Theme.fontWeight.regular};
  line-height: 19px;
  ${cssWordBreak}
  p {
    ${cssWordBreak}
  }
  @media (max-width: ${midCssBreak}) {
    margin-top: 8px;
  }
  @media (max-width: 700px) {
    font-size: 12px;
  }
  @media (max-width: ${smallCssBreak}) {
    display: none;
  }
`;

const InfoFooter = styled(Grid)`
  align-items: center;
  grid-column-gap: 5px;

  @media (max-width: ${midCssBreak}) {
    grid-template-columns: 30% 1fr 1fr;
    grid-column-gap: 0px;
  }
  @media (max-width: ${smallCssBreak}) {
    grid-template-columns: 100%;
  }

  ${cssIEGridSupportColumns};
`;

const LoadingFooter = styled(Grid)`
  @media (max-width: ${midCssBreak}) {
    grid-template-columns: 170px 1fr 100px;
  }
  @media (max-width: ${smallCssBreak}) {
    display: none;
  }
`;

const InfoMain = styled.div`
  padding: 10px 0 15px;
  @media (max-width: ${midCssBreak}) {
    padding-top: 2px;
  }
  @media (max-width: ${smallCssBreak}) {
    padding: 0 10px 10px;
    ${(props) => props.isLoading && 'padding-top: 40px'};
  }
`;

const InfoTime = styled.span`
  font-weight: ${Theme.fontWeight.regular};
  display: flex;
  align-self: center;

  height: 20px;
  align-items: center;
  svg {
    margin-right: 10px;
    fill: ${Theme.colors.line};
  }
  @media (max-width: ${smallCssBreak}) {
    font-size: 14px;
    margin-left: 10px;
  }
`;

const InfoPrice = styled.span`
  font-size: 14px;
  display: inline-block;
  text-align: center;

  strong {
    font-size: 18px;
    font-weight: 600;
    bottom: 2px;
  }

  @media (max-width: ${midCssBreak}) {
    font-size: 14px;
    strong {
      font-size: 16px;
    }
  }
  @media (max-width: ${smallCssBreak}) {
    color: white;
    position: absolute;
    font-size: 12px;
    top: 105px;
    right: 10px;
    text-align: right;
    display: block;
    strong {
      color: white;
      display: block;
      line-height: 20px;
    }
  }
`;

const ExperienceButton = styled(Button)`
  width: 100%;
  min-width: 96px;
  font-size: 16px;
  padding: 10px 20px;
  background-color: ${(props) => props.theme.buttonBackgroundColor};
  color: ${(props) => props.theme.buttonTextColor};
  border: none;

  @media (max-width: ${midCssBreak}) {
    min-width: 96px;
    padding: 6px 10px;
    height: 36px;
  }
  @media (max-width: ${smallCssBreak}) {
    width: 250px;
    margin: 10px -1px;
    border-radius: 0;
    outline: none;
    padding: 6px 10px;
    font-size: 14px;
  }
`;

const Loader = styled(ContentLoader)`
  display: block;
  @media (max-width: ${smallCssBreak}) {
    ${(props) => props.hideOnMobile && 'display: none;'};
  }
`;

const PictureLoader = styled.div`
  width: 250px;
  height: 250px;

  @media (max-width: ${midCssBreak}) {
    width: 210px;
    height: 210px;
  }
  @media (max-width: ${smallCssBreak}) {
    width: 248px;
    height: 250px;
  }
`;

function renderLoading() {
  return (
    <Card>
      <CardGrid gridTemplateColumns="250px 1fr" isLoading>
        <PictureLoader>
          <Loader />
        </PictureLoader>
        <Info gridTemplateRows="1fr 50px" isLoading>
          <InfoMain isLoading>
            <Loader
              style={{ display: 'block', marginBottom: '5px' }}
              height={18}
              width={230}
            />
            <Loader height={16} width={230} />
            <HR />
            <Loader height={36} width="80%" />
          </InfoMain>
          <LoadingFooter gridTemplateColumns="200px 1fr 90px">
            <Loader width={80} height={18} />
            <Loader width={60} height={18} />
            <Loader width={90} height={38} />
          </LoadingFooter>
        </Info>
      </CardGrid>
    </Card>
  );
}

export function ExperienceCard({ isLoading, experience }) {
  const isMobile = useMediaQuery({ query: `(max-width: ${smallCssBreak})` });

  if (isLoading) return renderLoading();

  const {
    picture,
    nameTranslation,
    taglineTranslation,
    mobilePicture,
    descriptionTranslation,
    length,
    zeroPriced,
    price,
    slug,
  } = experience;

  function renderPrice() {
    if (zeroPriced) return null;
    return (
      <InfoPrice>
        {t('FROM_PRICING')} <strong>{price}</strong>
      </InfoPrice>
    );
  }

  function renderTime() {
    const formattedLength = `${length} ${t('HOUR', false, {
      num: parseFloat(length),
    })}`;

    return (
      <InfoTime>
        <ClockIcon />
        {formattedLength}
      </InfoTime>
    );
  }

  return (
    <Card>
      <CardGrid gridTemplateColumns="265px 1fr">
        <GradientWrapper>
          <Picture src={picture} alt={nameTranslation} />
          <MobilePicture src={mobilePicture} alt={nameTranslation} />
        </GradientWrapper>
        <Info gridTemplateRows="1fr 50px">
          <InfoMain>
            <Name>{nameTranslation}</Name>
            <div>
              <Truncate lines={isMobile ? 2 : 1} trimWhitespace>
                <Tagline>{taglineTranslation}</Tagline>
              </Truncate>
            </div>
            <HR />
            <Description>{descriptionTranslation}</Description>
          </InfoMain>
          <InfoFooter gridTemplateColumns="140px 1fr 1fr">
            <div>{renderTime()}</div>
            <div>{renderPrice()}</div>

            <Link to={`${EXPERIENCE}#id=${slug}`}>
              <ExperienceButton data-testid="book-now-btn">
                {t('BOOK_NOW')}
              </ExperienceButton>
            </Link>
          </InfoFooter>
        </Info>
      </CardGrid>
    </Card>
  );
}
